import { useState } from "react";
import PropTypes from "prop-types";
import { Box, Alert, Snackbar, Card, alpha, Tabs, Tab } from "@mui/material";
import { History, PedalBike, People } from "@mui/icons-material";

import MDBox from "shared/components/MDBox";
import BicicletaModal from "../BicicletaModal";
import BicicletaModalSalida from "../BicicletaModalSalida";
import UsersTable from "../UsersTable";
import useFormBicicleta from "../../hooks/useFormBicicleta";
import { getCycleByQrService } from "modules/came/services/bicicletasService";

import ParkingOverview from "./ParkingOverview";
import MovementHistory from "./MovementHistory";

// Coordinates the existing movement forms; presentation lives in the tab components.
const FormBicicletas = ({ onIngreso, onSalida, refreshFlag, onError }) => {
  const formState = useFormBicicleta(onIngreso, onSalida, refreshFlag) || {};

  const ganchos = Array.isArray(formState.ganchos) ? formState.ganchos : [];
  const historico = Array.isArray(formState.historico) ? formState.historico : [];
  const users = Array.isArray(formState.users) ? formState.users : [];
  const parqueaderoActual = Array.isArray(formState.parqueaderoActual)
    ? formState.parqueaderoActual
    : [];

  const totalDisponibles = formState.totalDisponibles || 0;
  const totalOcupados = formState.totalOcupados || 0;

  const guardarUsuario = formState.guardarUsuario;
  const guardarBicicleta = formState.guardarBicicleta;
  const registrarCiclo = formState.registrarCiclo;

  const [tabValue, setTabValue] = useState(0);

  const [modo, setModo] = useState(null);
  const [ganchoSeleccionado, setGanchoSeleccionado] = useState(null);
  const [openModalIngreso, setOpenModalIngreso] = useState(false);
  const [openModalSalida, setOpenModalSalida] = useState(false);
  const [formData, setFormData] = useState({
    cedula: "",
    idType: 1,
    nombre: "",
    apellido: "",
    celular: "",
    color: "",
    marca: "",
    serie: "",
    ganchoId: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const openForm = (tipo) => {
    if (tipo === "SALIDA") {
      setOpenModalSalida(true);
      return;
    }

    setModo(tipo);
    setGanchoSeleccionado(null);
    setFormData({
      cedula: "",
      idType: 1,
      nombre: "",
      apellido: "",
      celular: "",
      color: "",
      marca: "",
      serie: "",
      ganchoId: "",
    });
    setOpenModalIngreso(true);
  };

  const closeModalIngreso = () => {
    setOpenModalIngreso(false);
    setGanchoSeleccionado(null);
  };

  const closeModalSalida = () => {
    setOpenModalSalida(false);
  };

  const handleFormDataChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleGanchoSelect = (ganchoId) => {
    const gancho = ganchos.find((g) => g.id === ganchoId);
    if (!gancho) return;

    if (modo === "INGRESO" && !gancho.disponible) {
      setSnackbar({ open: true, message: "Este gancho ya está ocupado", severity: "error" });
      return;
    }

    setGanchoSeleccionado(gancho);
    setFormData((prev) => ({ ...prev, ganchoId: ganchoId }));
  };

  const handleSaveUser = async (dataFromModal) => {
    if (!guardarUsuario) return { success: false, error: "Función no disponible" };
    const result = await guardarUsuario(dataFromModal);
    return result;
  };

  const handleSaveBike = async (dataFromModal, savedUser) => {
    if (!guardarBicicleta) return { success: false, error: "Función no disponible" };
    const result = await guardarBicicleta(dataFromModal, savedUser);
    return result;
  };

  const handleSubmitIngreso = async ({ formData: fd, user, bike }) => {
    if (!registrarCiclo) return { success: false, error: "Función no disponible" };

    const result = await registrarCiclo({
      ...fd,
      userId: user.id,
      bikeId: bike.id,
      ganchoId: ganchoSeleccionado?.id,
      modo: "INGRESO",
    });

    if (result?.success) {
      setSnackbar({
        open: true,
        message: "Ingreso registrado exitosamente",
        severity: "success",
      });
    } else {
      setSnackbar({ open: true, message: result?.error || "Error", severity: "error" });
      if (onError) onError(result?.error);
    }

    return result;
  };

  const handleScanQrSalida = async (qrCode) => {
    const result = await getCycleByQrService(qrCode);
    return result;
  };

  const handleRegistrarSalida = async (cycleData) => {
    if (!registrarCiclo) return { success: false, error: "Función no disponible" };

    const result = await registrarCiclo({
      userId: cycleData.user?.id,
      bikeId: cycleData.bike?.id,
      ganchoId: cycleData.harness,
      cicloId: cycleData.id,
      modo: "SALIDA",
    });

    if (result?.success) {
      setSnackbar({
        open: true,
        message: `Salida registrada: ${cycleData.user?.names || ""} ${
          cycleData.user?.lastnames || ""
        }`,
        severity: "success",
      });
    } else {
      setSnackbar({ open: true, message: result?.error || "Error", severity: "error" });
      if (onError) onError(result?.error);
    }

    return result;
  };

  return (
    <MDBox>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* MODALES */}
      {openModalIngreso && (
        <BicicletaModal
          open={openModalIngreso}
          onClose={closeModalIngreso}
          modo={modo}
          ganchos={ganchos}
          ganchoSeleccionado={ganchoSeleccionado}
          onGanchoSelect={handleGanchoSelect}
          formData={formData}
          onFormDataChange={handleFormDataChange}
          onSaveUser={handleSaveUser}
          onSaveBike={handleSaveBike}
          onSubmit={handleSubmitIngreso}
          users={users}
        />
      )}

      <BicicletaModalSalida
        open={openModalSalida}
        onClose={closeModalSalida}
        onScanQr={handleScanQrSalida}
        onRegistrarSalida={handleRegistrarSalida}
      />

      {/* TABS PRINCIPALES */}
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          border: "1px solid",
          borderColor: "divider",
          mb: 3,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: alpha("#1a237e", 0.02),
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              px: 2,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.85rem",
                minHeight: 56,
                "&.Mui-selected": { color: "#1a237e" },
              },
              "& .MuiTabs-indicator": { backgroundColor: "#1a237e", height: 3 },
            }}
          >
            <Tab
              icon={<PedalBike sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label="Ganchos y Acciones"
            />
            <Tab
              icon={<History sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label={`Histórico (${historico.length})`}
            />
            <Tab
              icon={<People sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label={`Usuarios (${users.length})`}
            />
          </Tabs>
        </Box>

        {/* TAB 0: GANCHOS Y ACCIONES */}
        {tabValue === 0 && (
          <ParkingOverview
            ganchos={ganchos}
            totalDisponibles={totalDisponibles}
            totalOcupados={totalOcupados}
            parqueaderoActual={parqueaderoActual}
            onIngreso={() => openForm("INGRESO")}
            onSalida={() => openForm("SALIDA")}
          />
        )}
        {tabValue === 1 && (
          <MovementHistory
            historico={historico}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        )}
        {/* TAB 2: USUARIOS */}
        {tabValue === 2 && <UsersTable refreshFlag={refreshFlag} />}
      </Card>
    </MDBox>
  );
};

FormBicicletas.propTypes = {
  onIngreso: PropTypes.func,
  onSalida: PropTypes.func,
  refreshFlag: PropTypes.bool,
  onError: PropTypes.func,
};

FormBicicletas.defaultProps = {
  onIngreso: null,
  onSalida: null,
  refreshFlag: false,
  onError: null,
};

export default FormBicicletas;
